import { apiBaseURL } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { fetchMandateKey, fetchMandate } from './fetch-mandate';

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  apiBaseURL: '/api',
}));

describe('unit | API | queries | fetch-mandate', () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const mandateId = 'mandate-123';
  const URL = `${apiBaseURL}/v1/direct_debit_collection_mandates/shared`;

  beforeEach(() => {
    vi.clearAllMocks();
  });

  describe(fetchMandateKey, () => {
    it('generates correct query key structure', () => {
      const result = fetchMandateKey(mandateId);

      expect(result).toStrictEqual(['mandate', mandateId]);
    });

    it('generates unique keys for different IDs', () => {
      const key1 = fetchMandateKey('mandate-1');
      const key2 = fetchMandateKey('mandate-2');

      expect(key1).not.toStrictEqual(key2);
    });
  });

  describe(fetchMandate, () => {
    it('fetches one-off mandate successfully', async () => {
      const apiResponse = {
        data: {
          id: mandateId,
          type: 'direct-debit-collection-mandates',
          attributes: {
            status: 'pending_signature',
            unique_mandate_reference: 'MANDATE-REF-123',
            creditor_identifier: 'CREDITOR-123',
            type: 'direct_debit',
            recurrence_type: 'one_off',
            link: 'https://example.com/mandate-link',
            client_iban_last_digits: '1234',
            client_bic: 'BIC123',
            client_snapshot: {
              id: 'client-123',
              name: 'Test Client',
              first_name: 'Test',
              last_name: 'Client',
              email: 'client@example.com',
              kind: 'individual',
              locale: 'en',
              address: '123 Test Street',
              city: 'Paris',
              zip_code: '75001',
              province_code: '',
              country_code: 'FR',
            },
            organization_snapshot: {
              id: 'org-123',
              name: 'Test Organization',
              logo_url: 'https://example.com/logo.png',
              address_line_1: '456 Org Street',
              address_line_2: '',
              city: 'Lyon',
              zip_code: '69001',
              country: 'FR',
            },
            payment_info: {
              first_payment: {
                collection_date: '2025-01-15T00:00:00Z',
                amount: {
                  value: '100.00',
                  currency: 'EUR',
                },
                reference: 'PAY-REF-123',
                is_overdue: false,
              },
              schedule_type: 'one_off',
              schedule_total_collection_count: 0,
              schedule_custom_unit: '',
              interval: 0,
              notify_client: true,
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchMandate({ id: mandateId });

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${mandateId}`);
      expect(result).toStrictEqual({
        mandateId,
        sddScheduleType: 'one_off',
        sddScheduleCount: 0,
        sddCustomScheduleUnit: '',
        sddCustomScheduleInterval: 0,
        sddAmount: {
          value: '100.00',
          currency: 'EUR',
        },
        sddCollectionDate: '2025-01-15T00:00:00Z',
        sddReference: 'PAY-REF-123',
        sddIsOverdue: false,
        clientName: 'Test Client',
        clientEmail: 'client@example.com',
        clientAddress: '123 Test Street',
        clientZipCode: '75001',
        clientCity: 'Paris',
        clientCountryCode: 'FR',
        clientProvinceCode: '',
        clientIban: '',
        clientLocale: 'en',
        organizationName: 'Test Organization',
        organizationAddress: '456 Org Street, 69001 Lyon, FR',
        organizationLogoUrl: 'https://example.com/logo.png',
        organizationCreditorIdentifier: 'CREDITOR-123',
        mandateReferenceCode: 'MANDATE-REF-123',
        addressWasPrefilled: true,
        isSddRecurring: false,
      });
    });

    it('fetches recurring mandate successfully', async () => {
      const apiResponse = {
        data: {
          id: mandateId,
          type: 'direct-debit-collection-mandates',
          attributes: {
            status: 'pending_signature',
            unique_mandate_reference: 'MANDATE-REF-456',
            creditor_identifier: 'CREDITOR-456',
            type: 'direct_debit',
            recurrence_type: 'recurring',
            link: 'https://example.com/mandate-link-456',
            client_iban_last_digits: '5678',
            client_bic: 'BIC456',
            client_snapshot: {
              id: 'client-456',
              name: 'Recurring Client',
              first_name: 'Recurring',
              last_name: 'Client',
              email: 'recurring@example.com',
              kind: 'individual',
              locale: 'fr',
              address: '456 Recurring Street',
              city: 'Lyon',
              zip_code: '69001',
              province_code: 'RA',
              country_code: 'FR',
            },
            organization_snapshot: {
              id: 'org-456',
              name: 'Recurring Organization',
              logo_url: 'https://example.com/logo-456.png',
              address_line_1: '789 Org Avenue',
              address_line_2: '',
              city: 'Marseille',
              zip_code: '13001',
              country: 'FR',
            },
            payment_info: {
              first_payment: {
                collection_date: '2025-02-01T00:00:00Z',
                amount: {
                  value: '500.00',
                  currency: 'EUR',
                },
                reference: 'PAY-REF-456',
                is_overdue: false,
              },
              schedule_type: 'monthly',
              schedule_total_collection_count: 12,
              schedule_custom_unit: 'month',
              interval: 1,
              notify_client: true,
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchMandate({ id: mandateId });

      expect(result).toStrictEqual({
        mandateId,
        sddScheduleType: 'monthly',
        sddScheduleCount: 12,
        sddCustomScheduleUnit: 'month',
        sddCustomScheduleInterval: 1,
        sddAmount: {
          value: '500.00',
          currency: 'EUR',
        },
        sddCollectionDate: '2025-02-01T00:00:00Z',
        sddReference: 'PAY-REF-456',
        sddIsOverdue: false,
        clientName: 'Recurring Client',
        clientEmail: 'recurring@example.com',
        clientAddress: '456 Recurring Street',
        clientZipCode: '69001',
        clientCity: 'Lyon',
        clientCountryCode: 'FR',
        clientProvinceCode: '',
        clientIban: '',
        clientLocale: 'fr',
        organizationName: 'Recurring Organization',
        organizationAddress: '789 Org Avenue, 13001 Marseille, FR',
        organizationLogoUrl: 'https://example.com/logo-456.png',
        organizationCreditorIdentifier: 'CREDITOR-456',
        mandateReferenceCode: 'MANDATE-REF-456',
        addressWasPrefilled: true,
        isSddRecurring: true,
      });
    });

    it('correctly identifies recurring mandate based on schedule_type', async () => {
      const apiResponse = {
        data: {
          id: mandateId,
          type: 'direct-debit-collection-mandates',
          attributes: {
            status: 'pending_signature',
            unique_mandate_reference: 'MANDATE-REF-WEEKLY',
            creditor_identifier: 'CREDITOR-WEEKLY',
            type: 'direct_debit',
            recurrence_type: 'recurring',
            link: 'https://example.com/mandate-link-weekly',
            client_iban_last_digits: '9999',
            client_bic: 'BICWEEKLY',
            client_snapshot: {
              id: 'client-weekly',
              name: 'Weekly Client',
              first_name: 'Weekly',
              last_name: 'Client',
              email: 'weekly@example.com',
              kind: 'individual',
              locale: 'en',
              address: '',
              city: '',
              zip_code: '',
              province_code: '',
              country_code: '',
            },
            organization_snapshot: {
              name: 'Test Org',
              logo_url: '',
              address: '',
              city: '',
              zip_code: '',
              country_code: '',
            },
            payment_info: {
              first_payment: {
                collection_date: '2025-01-20T00:00:00Z',
                amount: {
                  value: '50.00',
                  currency: 'EUR',
                },
                reference: 'PAY-REF-WEEKLY',
                is_overdue: false,
              },
              schedule_type: 'weekly',
              schedule_total_collection_count: 52,
              schedule_custom_unit: 'week',
              interval: 1,
              notify_client: false,
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchMandate({ id: mandateId });

      expect(result?.isSddRecurring).toBe(true);
      expect(result?.sddScheduleType).toBe('weekly');
      expect(result?.sddCustomScheduleUnit).toBe('week');
      expect(result?.sddCustomScheduleInterval).toBe(1);
      expect(result?.mandateReferenceCode).toBe('MANDATE-REF-WEEKLY');
      expect(result?.organizationCreditorIdentifier).toBe('CREDITOR-WEEKLY');
    });

    it('sets addressWasPrefilled to true when address fields are present', async () => {
      const apiResponse = {
        data: {
          id: mandateId,
          type: 'direct-debit-collection-mandates',
          attributes: {
            status: 'pending_signature',
            unique_mandate_reference: 'MANDATE-REF-ADDRESS',
            creditor_identifier: 'CREDITOR-ADDRESS',
            type: 'direct_debit',
            recurrence_type: 'one_off',
            link: 'https://example.com/mandate-link-address',
            client_iban_last_digits: '1111',
            client_bic: 'BICADDRESS',
            client_snapshot: {
              id: 'client-address',
              name: 'Address Client',
              first_name: 'Address',
              last_name: 'Client',
              email: 'address@example.com',
              kind: 'individual',
              locale: 'en',
              address: '123 Street',
              city: 'Paris',
              zip_code: '75001',
              province_code: 'IT-MI',
              country_code: 'FR',
            },
            organization_snapshot: {
              id: 'org-address',
              name: 'Test Org',
              logo_url: '',
              address_line_1: '',
              address_line_2: '',
              city: '',
              zip_code: '',
              country: '',
            },
            payment_info: {
              first_payment: {
                collection_date: '2025-01-25T00:00:00Z',
                amount: {
                  value: '75.00',
                  currency: 'EUR',
                },
                reference: 'PAY-REF-ADDRESS',
                is_overdue: false,
              },
              schedule_type: 'one_off',
              schedule_total_collection_count: 0,
              schedule_custom_unit: '',
              interval: 0,
              notify_client: true,
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchMandate({ id: mandateId });

      expect(result?.addressWasPrefilled).toBe(true);
      expect(result?.clientName).toBe('Address Client');
      expect(result?.clientEmail).toBe('address@example.com');
    });

    it('sets addressWasPrefilled to false when all address fields are empty', async () => {
      const apiResponse = {
        data: {
          id: mandateId,
          type: 'direct-debit-collection-mandates',
          attributes: {
            status: 'pending_signature',
            unique_mandate_reference: 'MANDATE-REF-EMPTY',
            creditor_identifier: 'CREDITOR-EMPTY',
            type: 'direct_debit',
            recurrence_type: 'one_off',
            link: 'https://example.com/mandate-link-empty',
            client_iban_last_digits: '2222',
            client_bic: 'BICEMPTY',
            client_snapshot: {
              id: 'client-empty',
              name: 'Empty Client',
              first_name: 'Empty',
              last_name: 'Client',
              email: 'empty@example.com',
              kind: 'individual',
              locale: 'en',
              address: '',
              city: '',
              zip_code: '',
              province_code: '',
              country_code: '',
            },
            organization_snapshot: {
              id: 'org-empty',
              name: 'Test Org',
              logo_url: '',
              address_line_1: '',
              address_line_2: '',
              city: '',
              zip_code: '',
              country: '',
            },
            payment_info: {
              first_payment: {
                collection_date: '2025-01-30T00:00:00Z',
                amount: {
                  value: '25.00',
                  currency: 'EUR',
                },
                reference: 'PAY-REF-EMPTY',
                is_overdue: false,
              },
              schedule_type: 'one_off',
              schedule_total_collection_count: 0,
              schedule_custom_unit: '',
              interval: 0,
              notify_client: false,
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchMandate({ id: mandateId });

      expect(result?.addressWasPrefilled).toBe(false);
      expect(result?.clientName).toBe('Empty Client');
      expect(result?.clientEmail).toBe('empty@example.com');
      expect(result?.mandateReferenceCode).toBe('MANDATE-REF-EMPTY');
    });

    it('handles API errors with custom error handler', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const onError = vi.fn<(err: unknown) => void>();
      await fetchMandate({ id: mandateId }, { onError });

      expect(onError).toHaveBeenCalledWith(error);
    });

    it('throws error when no error handler is provided', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      await expect(fetchMandate({ id: mandateId })).rejects.toThrow('API Error');
    });
  });
});
